/**
 * <estimated-delivery>
 * ----------------------------------------------------------------------------
 * Custom element that powers the "Estimated Delivery" widget shown in the PDP
 * Shipping & Returns accordion.
 *
 * Visual states (mirrored on the host element via `data-state`):
 *   - default        : showing computed estimate ("Est. Delivery to {pc}: ...")
 *   - edit           : input + Check button is visible
 *   - loading        : an API call is in-flight
 *   - error-invalid  : the suburb / postcode entered is not valid
 *   - error-api      : the AU Post endpoint failed (network / 5xx / etc.)
 *
 * INTEGRATION NOTES
 * ----------------------------------------------------------------------------
 * Suburb autocomplete uses a client-side JSON asset (assets/auspost-postcodes.json)
 * loaded lazily on first keystroke. Pass the asset URL via data-postcodes-url on
 * the host element (set in snippets/estimated-delivery.liquid).
 *
 * Delivery estimate (_fetchEstimate) prefers the server-side DJ task proxy when
 * data-estimate-url is configured and reachable:
 *   POST/GET /apps/auspost/estimate?postcode={postcode}
 *   → { startDate: 'YYYY-MM-DD', endDate: 'YYYY-MM-DD' }
 * If that endpoint is missing or fails, it falls back to a locally computed
 * business-days estimate (_localEstimate / _addBusinessDays) — the same logic
 * used by <shipping-info-component> in the boardriders-multi theme — driven by
 * data-min-days / data-max-days (default 2 / 6).
 */

// Module-level cache so the JSON is only fetched once across all widget instances.
let _postcodesData = null;
let _postcodesLoadPromise = null;

class EstimatedDelivery extends HTMLElement {
  constructor() {
    super();

    // Element references --------------------------------------------------
    this.defaultView = this.querySelector('[data-view="default"]');
    this.editView = this.querySelector('[data-view="edit"]');

    this.postcodeEl = this.querySelector('[data-postcode]');
    this.rangeText = this.querySelector('[data-range-text]');
    this.rangeLoading = this.querySelector('[data-range-loading]');

    this.form = this.querySelector('[data-form]');
    this.input = this.querySelector('[data-postcode-input]');
    this.suggestionsEl = this.querySelector('[data-suggestions]');
    this.errorEl = this.querySelector('[data-error]');
    this.checkBtn = this.querySelector('[data-action="check"]');
    this.checkLabel = this.querySelector('[data-check-label]');
    this.checkSpinner = this.querySelector('[data-check-spinner]');

    // Initial values ------------------------------------------------------
    this.defaultPostcode = this.dataset.defaultPostcode || '4220';
    this.currentPostcode = this.dataset.initialPostcode || this.defaultPostcode;
    this.postcodesUrl = this.dataset.postcodesUrl || null;
    this.estimateUrl = this.dataset.estimateUrl || null;
    this.minDays = parseInt(this.dataset.minDays, 10) || 2;
    this.maxDays = parseInt(this.dataset.maxDays, 10) || 6;
    this.selectedSuburb = null;
    this.suggestions = [];
    this.activeIndex = -1;
    this.suggestionsAbortController = null;
    this.suggestionsDebounce = null;
  }

  connectedCallback() {
    this._bindEvents();
    this._renderPostcode(this.currentPostcode);
    // Kick off an initial estimate so the default view shows real dates.
    this._loadEstimate(this.currentPostcode);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocumentClick);
    if (this.suggestionsAbortController) this.suggestionsAbortController.abort();
    clearTimeout(this.suggestionsDebounce);
  }

  // -----------------------------------------------------------------------
  // Event wiring
  // -----------------------------------------------------------------------
  _bindEvents() {
    this.querySelectorAll('[data-action="change"]').forEach((btn) => {
      btn.addEventListener('click', () => this._enterEditMode());
    });

    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this._onCheck();
      });
    }

    if (this.input) {
      this.input.addEventListener('input', () => this._onInput());
      this.input.addEventListener('keydown', (e) => this._onInputKeydown(e));
      this.input.addEventListener('focus', () => {
        if (this.suggestions.length > 0) this._showSuggestions();
      });
    }

    this._onDocumentClick = (e) => {
      if (!this.contains(e.target)) this._hideSuggestions();
    };
    document.addEventListener('click', this._onDocumentClick);
  }

  // -----------------------------------------------------------------------
  // State transitions
  // -----------------------------------------------------------------------
  _setState(state) {
    this.dataset.state = state;
  }

  _enterEditMode() {
    this._setState('edit');
    this._toggleViews(false);
    this._clearError();

    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }
  }

  _enterDefaultMode() {
    this._setState('default');
    this._toggleViews(true);
    this._clearError();
    this._hideSuggestions();
  }

  _toggleViews(isDefault) {
    if (this.defaultView) this.defaultView.hidden = !isDefault;
    if (this.editView) this.editView.hidden = isDefault;
  }

  _setLoading(isLoading) {
    if (this.checkBtn) this.checkBtn.disabled = isLoading;
    if (this.checkLabel) this.checkLabel.hidden = isLoading;
    if (this.checkSpinner) this.checkSpinner.hidden = !isLoading;
  }

  _setError(message) {
    if (!this.errorEl) return;
    if (message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    } else {
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
  }

  _clearError() {
    this._setError('');
  }

  // -----------------------------------------------------------------------
  // Input handling + autocomplete
  // -----------------------------------------------------------------------
  _onInput() {
    this._clearError();
    if (this.dataset.state === 'error-invalid' || this.dataset.state === 'error-api') {
      this._setState('edit');
    }

    const query = (this.input.value || '').trim();
    this.selectedSuburb = null;

    clearTimeout(this.suggestionsDebounce);

    if (query.length < 2) {
      this._hideSuggestions();
      return;
    }

    this.suggestionsDebounce = setTimeout(() => {
      this._loadSuggestions(query);
    }, 200);
  }

  _onInputKeydown(event) {
    if (this.suggestionsEl.hidden) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this._moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this._moveActive(-1);
        break;
      case 'Enter':
        if (this.activeIndex > -1 && this.suggestions[this.activeIndex]) {
          event.preventDefault();
          this._selectSuggestion(this.suggestions[this.activeIndex]);
        }
        break;
      case 'Escape':
        this._hideSuggestions();
        break;
    }
  }

  _moveActive(direction) {
    if (!this.suggestions.length) return;
    this.activeIndex =
      (this.activeIndex + direction + this.suggestions.length) % this.suggestions.length;
    this._highlightActive();
  }

  _highlightActive() {
    const items = this.suggestionsEl.querySelectorAll('li[data-suggestion]');
    items.forEach((li, idx) => {
      li.setAttribute('aria-selected', idx === this.activeIndex ? 'true' : 'false');
    });
  }

  _showSuggestions() {
    if (this.suggestionsEl) this.suggestionsEl.hidden = false;
  }

  _hideSuggestions() {
    if (this.suggestionsEl) this.suggestionsEl.hidden = true;
    this.activeIndex = -1;
  }

  _renderSuggestions(items) {
    if (!this.suggestionsEl) return;
    this.suggestionsEl.innerHTML = '';

    if (!items || items.length === 0) {
      const li = document.createElement('li');
      li.dataset.empty = 'true';
      li.textContent = 'No matching suburbs / postcodes';
      this.suggestionsEl.appendChild(li);
      this._showSuggestions();
      return;
    }

    items.forEach((item, idx) => {
      const li = document.createElement('li');
      li.dataset.suggestion = 'true';
      li.dataset.index = String(idx);
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      li.textContent = `${item.suburb}, ${item.state} ${item.postcode}`;
      li.addEventListener('click', () => this._selectSuggestion(item));
      this.suggestionsEl.appendChild(li);
    });

    this._showSuggestions();
  }

  _selectSuggestion(item) {
    this.selectedSuburb = item;
    this.input.value = `${item.suburb}, ${item.state} ${item.postcode}`;
    this._hideSuggestions();
  }

  async _loadSuggestions(query) {
    try {
      if (this.suggestionsAbortController) this.suggestionsAbortController.abort();
      this.suggestionsAbortController = new AbortController();

      const items = await this._fetchSuggestions(
        query,
        this.suggestionsAbortController.signal,
      );

      this.suggestions = items || [];
      this.activeIndex = -1;
      this._renderSuggestions(this.suggestions);
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      this._hideSuggestions();
    }
  }

  // -----------------------------------------------------------------------
  // Check button
  // -----------------------------------------------------------------------
  async _onCheck() {
    const rawValue = (this.input.value || '').trim();

    let postcode = null;
    if (this.selectedSuburb) {
      postcode = this.selectedSuburb.postcode;
    } else if (/^\d{4}$/.test(rawValue)) {
      postcode = rawValue;
    } else if (this.suggestions.length > 0 && this.suggestions[0].postcode) {
      postcode = this.suggestions[0].postcode;
      this._selectSuggestion(this.suggestions[0]);
    }

    if (!postcode) {
      this._setState('error-invalid');
      this._setError('Enter a valid suburb / postcode');
      return;
    }

    this._setLoading(true);
    this._clearError();

    const suburb = this.selectedSuburb ? this.selectedSuburb.suburb : null;
    const state  = this.selectedSuburb ? this.selectedSuburb.state  : null;

    try {
      const estimate = await this._fetchEstimate(postcode, suburb, state);
      this.currentPostcode = postcode;
      this._renderPostcode(postcode);
      this._renderEstimate(estimate);
      this._enterDefaultMode();
    } catch (err) {
      const isInvalid = err && err.code === 'INVALID_POSTCODE';
      if (isInvalid) {
        this._setState('error-invalid');
        this._setError('Enter a valid suburb / postcode');
      } else {
        this._setState('error-api');
        this._setError('Unable to check right now. Try again later');
      }
    } finally {
      this._setLoading(false);
    }
  }

  // -----------------------------------------------------------------------
  // Initial load
  // -----------------------------------------------------------------------
  async _loadEstimate(postcode) {
    if (!postcode) return;

    if (this.rangeLoading) this.rangeLoading.hidden = false;
    if (this.rangeText) this.rangeText.hidden = true;

    try {
      const estimate = await this._fetchEstimate(postcode);
      this._renderEstimate(estimate);
    } catch (err) {
      // Silent fail on initial load – widget stays in default state with a
      // generic message until the user retries via the "Change" link.
      if (this.rangeLoading) this.rangeLoading.textContent = 'Unavailable';
    }
  }

  _renderPostcode(postcode) {
    if (this.postcodeEl) this.postcodeEl.textContent = postcode;
  }

  _renderEstimate(estimate) {
    if (!estimate || !estimate.startDate || !estimate.endDate) return;

    const formatter = new Intl.DateTimeFormat('en-AU', {
      day: 'numeric',
      month: 'short',
    });

    const start = formatter.format(new Date(estimate.startDate));
    const end = formatter.format(new Date(estimate.endDate));

    if (this.rangeText) {
      this.rangeText.textContent = `${start} - ${end}`;
      this.rangeText.hidden = false;
    }
    if (this.rangeLoading) this.rangeLoading.hidden = true;
  }

  // -----------------------------------------------------------------------
  // ⤵ TODO: API INTEGRATION (Australia Post)
  // -----------------------------------------------------------------------
  /**
   * Fetch suburb / postcode autocomplete suggestions for a query string.
   * Loads assets/auspost-postcodes.json lazily on first call and caches the
   * result at module level so subsequent calls are instant.
   *
   * JSON format expected (compact array-of-arrays to minimise file size):
   *   [["SUBURB NAME","STATE","POSTCODE"], ...]
   *   e.g. [["Burleigh Heads","QLD","4220"], ...]
   *
   * Returns up to 10 results matching the query against suburb name prefix
   * or postcode prefix (case-insensitive).
   */
  async _fetchSuggestions(query, signal) {
    if (!this.postcodesUrl) return [];

    // Load + cache the dataset (shared across all widget instances).
    if (!_postcodesData) {
      if (!_postcodesLoadPromise) {
        _postcodesLoadPromise = fetch(this.postcodesUrl)
          .then((res) => {
            if (!res.ok) throw new Error('postcodes_load_failed');
            return res.json();
          })
          .then((raw) => {
            _postcodesData = raw.map(([suburb, state, postcode]) => ({
              suburb,
              state,
              postcode,
            }));
          })
          .catch(() => {
            _postcodesLoadPromise = null; // allow retry next time
          });
      }
      await _postcodesLoadPromise;
    }

    if (signal && signal.aborted) return [];
    if (!_postcodesData) return [];

    const q = query.toLowerCase();
    const results = [];
    for (let i = 0; i < _postcodesData.length && results.length < 10; i++) {
      const m = _postcodesData[i];
      if (m.suburb.toLowerCase().startsWith(q) || m.postcode.startsWith(q)) {
        results.push(m);
      }
    }
    return results;
  }

  /**
   * Fetch the estimated delivery window. Prefers the remote AusPost/DataJet
   * endpoint when `estimateUrl` is configured; falls back to a locally
   * computed business-days estimate (same approach as
   * <shipping-info-component> in the boardriders-multi theme) when no
   * endpoint is configured or the remote call fails.
   *
   * Returns { startDate: 'YYYY-MM-DD', endDate: 'YYYY-MM-DD' }.
   *
   * Throws Error with code === 'INVALID_POSTCODE' for 422 responses from the
   * remote endpoint; never throws otherwise (local fallback always succeeds).
   */
  async _fetchEstimate(postcode, suburb, state) {
    if (!this.estimateUrl) return this._localEstimate();

    try {
      const res = await fetch(this.estimateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postcode, suburb, state }),
      });

      if (res.status === 422) {
        const err = new Error('Invalid postcode');
        err.code = 'INVALID_POSTCODE';
        throw err;
      }

      if (!res.ok) throw new Error('estimate_failed');

      return await res.json();
    } catch (err) {
      if (err && err.code === 'INVALID_POSTCODE') throw err;
      return this._localEstimate();
    }
  }

  /**
   * Ported from <shipping-info-component> (assets/shipping-info.js in the
   * boardriders-multi theme): today + minDays/maxDays business days.
   */
  _localEstimate() {
    const start = this._addBusinessDays(new Date(), this.minDays);
    const end = this._addBusinessDays(new Date(), this.maxDays);
    return { startDate: this._toISODate(start), endDate: this._toISODate(end) };
  }

  _addBusinessDays(date, days) {
    const d = new Date(date.getTime());
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return d;
  }

  _toISODate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}

if (!customElements.get('estimated-delivery')) {
  customElements.define('estimated-delivery', EstimatedDelivery);
}
